import React, { useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE } from "../lib/site";

const NAV = [
  { label: "Services", href: "/#services" },
  { label: "Pricing", href: "/pricing", route: true },
  { label: "Scam Help", href: "/#scam-help" },
  { label: "About", href: "/#about" },
  { label: "FAQs", href: "/#faq" },
  { label: "Book a Visit", href: "/#book" },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-[#FFFFFF]/95 backdrop-blur border-b border-[#DCE4EC]"
    >
      <div className="bg-[#0F2E52] text-[#FFFFFF] text-xs md:text-sm">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-2 flex items-center justify-between gap-3">
          <a
            href={`tel:${SITE.phoneTel}`}
            data-testid="ribbon-phone-cta"
            className="inline-flex items-center gap-2 font-bold text-sm md:text-base hover:text-[#6BAF3C] transition-colors whitespace-nowrap"
          >
            <Phone className="w-4 h-4" strokeWidth={2.5} />
            <span>{SITE.phone}</span>
            <span className="hidden sm:inline text-white/90 font-semibold">· Call to book a home visit</span>
          </a>
          <span className="text-white/90 text-right hidden sm:inline">
            I come to your home · No overseas call centres
          </span>
          <span className="text-white/90 text-right sm:hidden font-semibold text-[11px] leading-tight">
            Visiting your home anywhere in Adelaide
          </span>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
        <a
          href="#top"
          data-testid="header-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={SITE.logo}
            alt="The Patient Tech Bloke logo"
            className="w-12 h-12 md:w-14 md:h-14 object-contain shrink-0"
          />
          <div className="leading-tight min-w-0">
            <div className="font-[Work_Sans] font-black text-lg sm:text-xl md:text-2xl tracking-tight truncate">
              <span className="text-[#0F2E52]">The Patient </span>
              <span className="text-[#6BAF3C]">Tech Bloke</span>
            </div>
            <div className="text-xs sm:text-sm md:text-base italic text-[#475569] truncate">
              <span className="text-[#0F2E52] font-semibold not-italic">Friendly.</span>{" "}
              <span className="text-[#334155] font-semibold not-italic">Patient.</span>{" "}
              <span className="text-[#6BAF3C] font-semibold not-italic hand-underline">Local.</span>
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV.map((n) =>
            n.route ? (
              <Link
                key={n.href}
                to={n.href}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-base font-semibold text-[#1E3A5F] hover:text-[#0F2E52] transition-colors"
              >
                {n.label}
              </Link>
            ) : (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-base font-semibold text-[#1E3A5F] hover:text-[#0F2E52] transition-colors"
              >
                {n.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={`tel:${SITE.phoneTel}`}
            data-testid="header-phone-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-[#0F2E52] hover:bg-[#0A2340] text-white font-bold rounded-xl px-5 py-3 text-base md:text-lg shadow-sm transition-colors min-h-[52px] whitespace-nowrap"
          >
            <Phone className="w-5 h-5" strokeWidth={2.5} />
            <span>{SITE.phone}</span>
          </a>
          <a
            href={`tel:${SITE.phoneTel}`}
            data-testid="header-phone-icon"
            aria-label={`Call Tom on ${SITE.phone}`}
            className="sm:hidden w-12 h-12 rounded-xl bg-[#0F2E52] hover:bg-[#0A2340] text-white grid place-items-center shadow-sm"
          >
            <Phone className="w-6 h-6" strokeWidth={2.5} />
          </a>
          <button
            type="button"
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-3 rounded-xl border border-[#DCE4EC] text-[#0F2E52] hover:bg-[#F0F7E8]"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#DCE4EC] bg-[#FFFFFF]" data-testid="mobile-menu">
          <div className="max-w-6xl mx-auto px-5 py-4 flex flex-col gap-2">
            {NAV.map((n) =>
              n.route ? (
                <Link
                  key={n.href}
                  to={n.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="py-3 px-3 text-lg font-semibold text-[#1E3A5F] rounded-lg hover:bg-[#F0F7E8]"
                >
                  {n.label}
                </Link>
              ) : (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="py-3 px-3 text-lg font-semibold text-[#1E3A5F] rounded-lg hover:bg-[#F0F7E8]"
                >
                  {n.label}
                </a>
              )
            )}
            <a
              href={`tel:${SITE.phoneTel}`}
              onClick={() => setOpen(false)}
              data-testid="mobile-phone-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#0F2E52] text-white font-bold rounded-xl px-5 py-4 text-lg"
            >
              <Phone className="w-5 h-5" /> Call {SITE.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
